import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Document, Page, Text, View, StyleSheet, PDFDownloadLink } from '@react-pdf/renderer';
import PDFLogo from '@/components/pdf/PDFLogo';
import { Header } from '../components/shared/Header/Header';
import { getRecommendations } from "@/_constants/getRecommendations";
import LoadingState from '../components/shared/LoadingState/LoadingState';
import ErrorState from '../components/shared/LoadingState/ErrorState';
import DownloadIcon from '@/components/icons/DownloadIcon';
import PrintIcon from '@/components/icons/PrintIcon';


const pdfStyles = StyleSheet.create({
  page: {
    backgroundColor: '#ffffff',
    padding: 40,
    paddingTop: 70,
    paddingBottom: 50,
    fontFamily: 'Helvetica',
  },
  headerBar: {
    position: 'absolute',
    top: 20,
    left: 40,
    right: 40,
    height: 30,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  logo: {
    width: 90,
    height: 22,
  },
  footer: {
    position: 'absolute',
    bottom: 20,
    left: 40,
    right: 40,
    textAlign: 'center',
    fontSize: 9,
    color: '#6b7280',
  },
  container: {
    flex: 1,
  },
  heroHeader: {
    backgroundColor: '#059669',
    borderRadius: 8,
    padding: 16,
    marginTop: 8,
    marginBottom: 24,
  },
  heroTitle: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  heroSubtitle: {
    color: '#e5e7eb',
    fontSize: 10,
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 50,
    textAlign: 'left',
    color: '#000000',
  },
  infoSection: {
    marginBottom: 30,
  },
  infoRow: {
    flexDirection: 'row',
    marginBottom: 15,
    alignItems: 'flex-end',
  },
  label: {
    fontSize: 11,
    fontWeight: 'bold',
    width: 120,
    color: '#000000',
  },
  valueContainer: {
    flex: 1,
    marginLeft: 10,
    paddingBottom: 2,
  },
  value: {
    fontSize: 11,
    color: '#000000',
  },
  card: {
    marginTop: 16,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 8,
    padding: 14,
  },
  cardHeader: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  numberBadge: {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: '#059669',
    color: '#ffffff',
    textAlign: 'center',
    fontSize: 9,
    fontWeight: 'bold',
    paddingTop: 3,
  },
  cardTitle: {
    fontSize: 11,
    color: '#374151',
    fontWeight: 'bold',
    marginLeft: 8,
  },
  descriptionBox: {
    backgroundColor: '#ecfdf5',
    borderColor: '#a7f3d0',
    borderWidth: 1,
    borderRadius: 6,
    padding: 10,
    marginBottom: 8,
  },
  descriptionLead: {
    fontSize: 11,
    fontWeight: 'bold',
    color: '#065f46',
    lineHeight: 1.25,
  },
  descriptionRest: {
    fontSize: 10,
    color: '#065f46',
    lineHeight: 1.25,
  },
  recommendationTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 20,
    color: '#000000',
  },
  actionStep: {
    marginBottom: 14,
  },
  actionStepTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 8,
    marginTop: 6,
    color: '#000000',
    lineHeight: 1.3,
  },
  actionStepContent: {
    fontSize: 11,
    lineHeight: 1.4,
    padding: 0,
    backgroundColor: 'transparent',
    color: '#333333',
    marginTop: 4
  },
  divider: {
    height: 1,
    backgroundColor: '#f3f4f6',
    marginTop: 8,
    marginBottom: 8,
  },
  notesSection: {
    marginTop: 30,
    paddingTop: 20,
    
  },
  notesTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 20,
    color: '#000000',
  },
  notesBox: {
    minHeight: 100,
    backgroundColor: '#ffffff',
    padding: 12,
  },
});

const splitStepText = (step: string): { title: string; body: string } => {
  const clean = step.replace(/"/g, '').trim();
  const colonIndex = clean.indexOf(':');
  if (colonIndex !== -1) {
    return {
      title: clean.slice(0, colonIndex).trim(),
      body: clean.slice(colonIndex + 1).trim(),
    };
  }
  return { title: clean, body: '' };
};

const splitDescription = (desc: string): { lead: string; rest: string } => {
  const clean = desc.replace(/"/g, '').trim();
  const dashIndex = clean.search(/[-–—]/);
  if (dashIndex > 0) {
    return {
      lead: clean.slice(0, dashIndex).trim(),
      rest: clean.slice(dashIndex + 1).trim(),
    };
  }
  const colonIndex = clean.indexOf(':');
  if (colonIndex > 0) {
    return {
      lead: clean.slice(0, colonIndex).trim(),
      rest: clean.slice(colonIndex + 1).trim(),
    };
  }
  return { lead: clean, rest: '' };
};

const SubdomainRecommendationsPDFDocument: React.FC<{ data: RecommendationData }> = ({ data }) => {
  const { t } = useTranslation();
  
  console.log('PDF Document Data:', {
    domain: data.domain,
    subdomain: data.subdomain,
    score: data.score,
    recommendationSets: data.recommendationSets.length
  });
  
  return (
    <Document>
      <Page size="A4" style={pdfStyles.page} wrap>
        <View style={pdfStyles.headerBar}>
          <PDFLogo width={90} height={22} />
        </View>
        <View style={pdfStyles.container}>
          <Text style={pdfStyles.title}>Recommendations</Text>

          <View style={pdfStyles.heroHeader}>
            <Text style={pdfStyles.heroTitle}>{data.subdomain}</Text>
            <Text style={pdfStyles.heroSubtitle}>{data.domain} • Current Score: {data.score}</Text>
          </View>

          <View style={pdfStyles.infoSection}>
            <View style={pdfStyles.infoRow}>
              <Text style={pdfStyles.label}>{t('recommendations.domain')}:</Text>
              <View style={pdfStyles.valueContainer}>
                <Text style={pdfStyles.value}>{data.domain}</Text>
              </View>
            </View>

            <View style={pdfStyles.infoRow}>
              <Text style={pdfStyles.label}>{t('recommendations.subdomain')}:</Text>
              <View style={pdfStyles.valueContainer}>
                <Text style={pdfStyles.value}>{data.subdomain}</Text>
              </View>
            </View>

            <View style={pdfStyles.infoRow}>
              <Text style={pdfStyles.label}>{t('recommendations.currentScore')}:</Text>
              <View style={pdfStyles.valueContainer}>
                <Text style={pdfStyles.value}>{data.score}</Text>
              </View>
            </View>
          </View>

          {data.recommendationSets.map((recSet, index) => {
            const { lead, rest } = splitDescription(recSet.description || '');
            return (
              <View key={index} style={pdfStyles.card} wrap={false}>
                <View style={pdfStyles.cardHeader}>
                  <Text style={pdfStyles.numberBadge}>{String(index + 1)}</Text>
                  <Text style={pdfStyles.cardTitle}>{t('recommendations.recommendationSet')} {index + 1}</Text>
                </View>
                {recSet.description ? (
                  <View style={pdfStyles.descriptionBox}>
                    <Text style={pdfStyles.descriptionLead}>{lead}</Text>
                    {rest ? <Text style={pdfStyles.descriptionRest}> — {rest}</Text> : null}
                  </View>
                ) : null}
                {recSet.actionSteps.map((step, stepIndex) => {
                  const { title: stepTitle, body: stepBody } = splitStepText(step);
                  return (
                    <View key={stepIndex} style={pdfStyles.actionStep}>
                      <Text style={pdfStyles.actionStepTitle}>
                        {t('recommendations.actionStep')} {stepIndex + 1}: {stepTitle}
                      </Text>
                      {stepBody ? (
                        <View style={pdfStyles.actionStepContent}>
                          <Text>{stepBody}</Text>
                        </View>
                      ) : null}
                      {stepIndex < recSet.actionSteps.length - 1 ? <View style={pdfStyles.divider} /> : null}
                    </View>
                  );
                })}
              </View>
            );
          })}

          <View style={pdfStyles.notesSection} wrap={false}>
            <Text style={pdfStyles.notesTitle}>{t('recommendations.implementationNotes')}:</Text>
            <View style={pdfStyles.notesBox} />
          </View>
        </View>
        <Text style={pdfStyles.footer}>Copyright @ 2025 ViaTelos. All Rights Reserved.</Text>
      </Page>
    </Document>
  );
};

interface RecommendationSet {
  recommendationNumber: string;
  description: string;
  actionSteps: string[];
}

interface RecommendationData {
  domain: string;
  subdomain: string;
  score: string;
  phaseTransition?: string;
  recommendationSets: RecommendationSet[];
}

const RecommendationsContent: React.FC<{ 
  recommendationData: RecommendationData; 
}> = ({ recommendationData }) => {
  const { t } = useTranslation();
  return (
    <div className="">
      {/* Header banner */}
      <div className="mt-4 rounded-xl overflow-hidden bg-gradient-to-r from-emerald-600 to-emerald-700 text-white shadow mx-2 sm:mx-0">
        <div className="px-4 sm:px-6 py-4 sm:py-5">
          <div className="text-xs sm:text-sm opacity-90">{recommendationData.domain}</div>
          <div className="text-lg sm:text-xl md:text-2xl font-semibold mt-1">{recommendationData.subdomain}</div>
          <div className="mt-1 sm:mt-2 text-xs opacity-90">
            {recommendationData.phaseTransition} {recommendationData.phaseTransition ? '•' : ''} {t('recommendations.currentScore')}: {recommendationData.score}
          </div>
        </div>
      </div>

      {/* Cards grid */}
      <div className="grid gap-4 sm:gap-6 md:grid-cols-2 mt-4 sm:mt-6 px-2 sm:px-0">
        {recommendationData.recommendationSets.map((recSet, index) => (
          <div key={index} className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 sm:p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-emerald-600 text-white text-xs font-bold">
                  {index + 1}
                </span>
              </div>
            </div>
            {recSet.description && (() => {
              const { lead, rest } = splitDescription(recSet.description);
              return (
                <div className="bg-emerald-500/10 border border-emerald-200 rounded-md p-3 mb-3">
                  <p className="text-xs sm:text-sm text-gray-800 leading-relaxed">
                    <span className="font-semibold">{lead}</span>
                    {rest ? ` — ${rest}` : ''}
                  </p>
                </div>
              );
            })()}
            <div className="space-y-3">
              {recSet.actionSteps.map((step, stepIndex) => {
                const { title: stepTitle, body: stepBody } = splitStepText(step);
                return (
                  <div key={stepIndex}>
                    <div className="text-xs sm:text-sm font-semibold text-gray-900">
                      {t('recommendations.actionStep')} {stepIndex + 1}: {stepTitle}
                    </div>
                    {stepBody && (
                      <p className="text-xs sm:text-sm text-gray-700 leading-relaxed mt-1">{stepBody}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const PDFDownloadButton = ({ recommendationData }: { recommendationData: RecommendationData }) => {
  const { t } = useTranslation();
  return (
    <PDFDownloadLink
      document={<SubdomainRecommendationsPDFDocument data={recommendationData} />}
      fileName={`${recommendationData.subdomain.toLowerCase().replace(/\s+/g, '-')}-recommendations.pdf`}
      className="bg-gray-800 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors flex items-center justify-center gap-2"
   >
      {({ loading: pdfLoading }: { loading: boolean }) => (
        <>
          <DownloadIcon className="w-5 h-5" />
          <span className="hidden sm:inline text-sm sm:text-base">
            {pdfLoading ? t('recommendations.generating') : t('recommendations.downloadPDF')}
          </span>
        </>
      )}
    </PDFDownloadLink>
  );
};

const RecommendationsHeader = ({ recommendationData }: { recommendationData: RecommendationData }) => {
  const { t } = useTranslation();
  return (
    <Header 
      rightContent={
        <div className="flex flex-row gap-2">
          <PDFDownloadButton  recommendationData={recommendationData} />
          <button
            onClick={() => window.print()}
            className="bg-[#5E9E2E] text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-[#4c8025] transition-colors flex items-center justify-center gap-2"
            type="button"
          >
            <PrintIcon className="w-5 h-5" />
            <span className="hidden sm:inline text-sm sm:text-base">{t('print')}</span>
          </button>
        </div>
      }
    />
  );
};

const SubdomainRecommendationsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [recommendationData, setRecommendationData] = useState<RecommendationData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const domainName = params.get("domainName");
  const subdomainName = params.get("subdomainName");
  const score = params.get("score");

  console.log({
    domainName,
    subdomainName,
    score
  });

  useEffect(() => {
    const loadRecommendationsData = async () => {
      try {
        setLoading(true);

        if (!domainName || !subdomainName || !score) {
          setRecommendationData(null);
          return;
        }

        const data = getRecommendations(domainName, subdomainName, score);
        if (!data) {
          setRecommendationData(null);
          return;
        }

        setRecommendationData(data);
      } catch (err) {
        console.error('Error loading recommendations data:', err);
        setRecommendationData(null);
      } finally {
        setLoading(false);
      }
    };

    loadRecommendationsData();
  }, [domainName, subdomainName, score]);

  const handleGoBack = () => {
    navigate(-1);
  };

  if (loading) {
    return <LoadingState message={t('recommendations.loadingRecommendations')} />;
  }

  if (!recommendationData) {
    return (
      <ErrorState 
        message={t('recommendations.noRecommendationsFound')}
        buttonText={t('common.goBack')}
        onButtonClick={handleGoBack}
      />
    );
  }

  return (
    <div>
      <RecommendationsHeader recommendationData={recommendationData} />
      <RecommendationsContent recommendationData={recommendationData} />
    </div>
  );
};

export default SubdomainRecommendationsPage;